// Real Trustpilot 5-star reviews of jobtest.org. Sourced from
// https://www.trustpilot.com/review/jobtest.org?search=report&stars=5
//
// We surface a persona-matched subset on /pricing — reviews are sorted
// against the user's quiz tags (career switcher, returner, etc.) so the
// most relatable testimonials show up first. The body is preserved
// largely verbatim where possible; very long reviews are condensed
// while keeping the writer's voice and not embellishing claims.

export interface Review {
  id: string;
  rating: 1 | 2 | 3 | 4 | 5;
  title: string;
  body: string;
  author: string;
  /** ISO date string */
  date: string;
  /** Country, e.g. "US" */
  country?: string;
  /** Verified review flag */
  verified?: boolean;
  /** Quiz-context tags used by /pricing to surface persona-matched reviews
   *  first. Examples: 'careerSwitch' | 'advance' | 'lost' | 'return'
   *  | 'burnout' | 'salary' | 'coaching' | 'pivot' | 'midcareer'. */
  tags?: string[];
}

export const REVIEWS: Review[] = [
  {
    id: 'r-jaxson',
    rating: 5,
    title: 'Got me a much higher-paying job',
    body: 'Zanetta helped me get a much higher paying job. She was especially helpful with focusing on what I should pursue and the job interview process. Well worth the money.',
    author: 'Jaxson',
    date: '2026-04-02',
    country: 'US',
    verified: true,
    tags: ['salary', 'advance', 'coaching'],
  },
  {
    id: 'r-hyunmi',
    rating: 5,
    title: 'Stay-Home-Mom to Working Mom',
    body: "When I got pregnant, I quit my job and took care of my son for the past 20 years. Now that my son is off to college, I wanted to return to the workforce but didn't know what to do. I'd taken other career tests but they all felt like they were meant for college students. JobTest's report recommended Office Manager at a startup — I was always fascinated by startups but never knew I had the skillsets. After reading the report I decided to give it a try and I'm happy to announce I accepted the job offer.",
    author: 'Hyunmi L.',
    date: '2026-03-08',
    country: 'KR',
    verified: true,
    tags: ['return', 'midcareer', 'pivot'],
  },
  {
    id: 'r-sharlenne',
    rating: 5,
    title: 'I even cried reading the report',
    body: "I even cried reading the report. It's been a whole life not really knowing what I want, yet it all makes so much sense now!",
    author: 'Sharlenne M.',
    date: '2026-04-15',
    country: 'US',
    verified: true,
    tags: ['lost', 'careerSwitch'],
  },
  {
    id: 'r-park',
    rating: 5,
    title: "It's worth the money",
    body: "I tried JobTest and was just going to enjoy the free version for fun. But the result really caught my attention, and I went to pay for the full report. It shed light on hidden strong-points and characteristics I've never realized, and now I think I found a path toward my dream job. For anyone lost in their career — give it a shot. You'll be surprised.",
    author: 'Park S.',
    date: '2026-03-21',
    country: 'US',
    verified: true,
    tags: ['lost', 'careerSwitch'],
  },
  {
    id: 'r-tess',
    rating: 5,
    title: 'A worthy investment',
    body: "I got the basic report package and was pleased with the results. I wasn't surprised I got content strategist — it's a job I'd told the report I was interested in. But I WAS surprised when UX Designer was on the list. There's a lot of good info in these reports and it would be very helpful for anyone considering changing careers or fresh out of school.",
    author: 'Tess A.',
    date: '2026-04-22',
    country: 'US',
    verified: true,
    tags: ['careerSwitch', 'pivot'],
  },
  {
    id: 'r-megan',
    rating: 5,
    title: 'Uplifting, critical without being insulting',
    body: "Yes, there's a paywall at the end. You should still take the test! WOW — personality type, potential monthly salary, detailed analysis of strengths and weaknesses with HELPFUL suggestions to improve. The results are very uplifting, critical without being insulting, and inspiring all around. If you're struggling with unlocking your full career potential, I highly recommend.",
    author: 'Megan C.',
    date: '2026-03-29',
    country: 'US',
    verified: true,
    tags: ['lost', 'salary'],
  },
  {
    id: 'r-shelby',
    rating: 5,
    title: 'Absolutely worth the money — better than expected',
    body: "I was super hesitant to spend money on this type of thing because I'm a skeptic, and frankly I'd already spent money this year on job hunting. But I'M SO GLAD I DID. The report is robust and detailed, with legitimate data and clear next steps. Definitely would recommend.",
    author: 'Shelby M.',
    date: '2026-04-09',
    country: 'US',
    verified: true,
    tags: ['advance', 'careerSwitch'],
  },
  {
    id: 'r-joshua',
    rating: 5,
    title: "Should've done this years ago",
    body: "Test took about 20 minutes. I'd taken this test before a couple years ago but never bought a report — I regret doing that. Now that I've bought the comprehensive report I have a clear career goal.",
    author: 'Joshua',
    date: '2026-02-25',
    country: 'US',
    verified: true,
    tags: ['lost', 'advance'],
  },
  {
    id: 'r-cristopher',
    rating: 5,
    title: 'They helped me see beyond my existing field',
    body: "I took the test and the advice and justification they gave for the recommendations were extremely good. They helped me see beyond my existing field and what I could really be working toward if I wanted. The detailed report is well worth the money if you're trying to understand yourself.",
    author: 'Cristopher C.',
    date: '2026-03-17',
    country: 'US',
    verified: true,
    tags: ['careerSwitch', 'pivot', 'lost'],
  },
  {
    id: 'r-althea',
    rating: 5,
    title: 'Great value and insightful career recommendations',
    body: "The cost for the detailed report and access to the platform is reasonable. The assessment considers personality, values, work history and future aspirations in suggestions for potential career paths. The career prospects excited me — and I will follow JobTest's top recommendation for me.",
    author: 'Althea B.',
    date: '2026-04-04',
    country: 'US',
    verified: true,
    tags: ['careerSwitch', 'advance'],
  },
  {
    id: 'r-lauren',
    rating: 5,
    title: 'Created a realistic action plan',
    body: "Jen was great and spent time helping me verbalize what I was really looking for and connecting that to the information provided in the report. She provided a TON of resources I had no idea about, and we created a realistic action plan for me to navigate the new career path I'm looking at.",
    author: 'Lauren',
    date: '2026-03-25',
    country: 'US',
    verified: true,
    tags: ['careerSwitch', 'coaching', 'pivot'],
  },
  {
    id: 'r-daniel',
    rating: 5,
    title: 'Every session paid off tenfold',
    body: "Looking for a new career is one of the greatest trials in many people's lives. My coach & mentor served as a bright light on my journey — patient, yet assertive, helping me decide next steps and keep things in perspective. I wish I could give JobTest 6 stars. Every session paid off tenfold and I am so grateful.",
    author: 'Daniel F.',
    date: '2026-04-12',
    country: 'US',
    verified: true,
    tags: ['coaching', 'careerSwitch'],
  },
  {
    id: 'r-cch',
    rating: 5,
    title: 'Paying for the report was worth it',
    body: "I enjoyed the amount of questions asked — they were needed to get to the bottom of what the test taker is looking for. The little statistics shared between questions were informative. Paying for a report was worth it. This one-time payment can be worth the abundance of opportunities afterwards if it connects me with the right job suggestions.",
    author: 'C Ch',
    date: '2026-04-05',
    country: 'US',
    verified: true,
    tags: ['lost', 'advance'],
  },
  {
    id: 'r-kamerin',
    rating: 5,
    title: 'Perfect tool for self-marketing',
    body: "The career report was exactly what I needed to kickstart my motivation and direction of my job search. These small details about me, fashioned elaborately in ways I could recycle and utilize myself, are the perfect bit of support to feel confident in marketing myself to employers.",
    author: 'Kamerin T.',
    date: '2026-03-30',
    country: 'US',
    verified: true,
    tags: ['advance', 'careerSwitch'],
  },
  {
    id: 'r-megan-roadmap',
    rating: 5,
    title: 'A clear roadmap, not just a job title',
    body: "The test was helpful and I like that the report goes into personality as well as several career trajectories that could be a good fit. Validated a few ideas I already had, and lays out a roadmap to how to progress into those kinds of roles. Acquiring some of those advanced skills seems daunting but everyone has to start as a beginner sometime.",
    author: 'Megan',
    date: '2026-03-12',
    country: 'US',
    verified: true,
    tags: ['advance', 'careerSwitch'],
  },
  {
    id: 'r-justin',
    rating: 5,
    title: 'Does the job — justified the price',
    body: "Does the job, and you get what you pay for. Is it the same level as what you get from a $200 career counselor? No. I paid for the report, and it justified the price.",
    author: 'Justin',
    date: '2026-04-19',
    country: 'US',
    verified: true,
    tags: ['advance'],
  },
  {
    id: 'r-joe',
    rating: 5,
    title: 'Enlightened me to many options I hadn\'t considered',
    body: "I learned about fields that I may be a candidate for with the related experience I already have, which was very inspiring. It was a good dialogue on the call, and the report generated for me afterwards was extensive with great resources.",
    author: 'Joe',
    date: '2026-03-19',
    country: 'US',
    verified: true,
    tags: ['careerSwitch', 'pivot', 'lost'],
  },
  {
    id: 'r-jordan',
    rating: 5,
    title: 'Surprisingly comprehensive',
    body: "The test took a minimal amount of time and the layout of the results were surprisingly comprehensive.",
    author: 'Jordan J.',
    date: '2026-04-01',
    country: 'US',
    verified: true,
    tags: ['advance', 'careerSwitch'],
  },
  {
    id: 'r-cathy',
    rating: 5,
    title: 'Career paths I had not considered',
    body: "I just received my report and am about to explore the job recommendations. I'm so excited! There are career paths I have not considered. I'm going to be up all night diving deep into the information provided.",
    author: 'Cathy A.',
    date: '2026-04-23',
    country: 'US',
    verified: true,
    tags: ['careerSwitch', 'pivot'],
  },
  {
    id: 'r-juan',
    rating: 5,
    title: 'A useful tool for better career decisions',
    body: "It all starts with a questionnaire that covers a range of topics from background, to personality, to previous job experiences, to future aspirations. Once complete, the website generates a really cool report with job recommendations and insights. There is a great effort to provide people with a useful tool for making better career decisions.",
    author: 'Juan M.',
    date: '2026-03-26',
    country: 'CA',
    verified: true,
    tags: ['lost', 'careerSwitch'],
  },
  {
    id: 'r-brian',
    rating: 5,
    title: 'The report was amazing',
    body: "The report was amazing and analyzed independently. I am grateful.",
    author: 'Brian O.',
    date: '2026-02-14',
    country: 'KE',
    verified: true,
    tags: ['advance', 'careerSwitch'],
  },
];

export const REVIEW_STATS = {
  averageRating: 4.9,
  totalCount: 750,
};
